import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FraudQueueParams, getFraudOpenCount, getFraudQueue, getFraudUserDetail, postFraudAction } from 'src/api/fraudWatchApi';
import { FraudQueueResponse, FraudUserDetail } from 'src/types/fraudWatch.type';
import { MutationParams } from './useProgram';

export const FraudWatchQueryKeys = {
   GetQueue: 'getFraudQueue',
   GetUserDetail: 'getFraudUserDetail',
   GetOpenCount: 'getFraudOpenCount',
   PostAction: 'postFraudAction',
};

export const useGetFraudQueue = (query: FraudQueueParams) => {
   return useQuery({
      queryKey: [FraudWatchQueryKeys.GetQueue, query],
      queryFn: () => getFraudQueue(query),
      select: (response): FraudQueueResponse => ({
         entities: response?.data?.entities ?? [],
         totalEntities: response?.data?.totalEntities ?? 0,
      }),
      staleTime: Infinity,
      retry: 0,
      placeholderData: prev => prev,
   });
};

// Polls while the admin has the page open — the whole point of the live view is
// seeing an in-flight session, and React Query stops the interval on unmount.
export const useGetFraudUserDetail = (userId?: string) => {
   return useQuery({
      queryKey: [FraudWatchQueryKeys.GetUserDetail, userId],
      queryFn: () => getFraudUserDetail(userId as string),
      select: (response): FraudUserDetail | undefined => response?.data,
      refetchInterval: 10_000,
      refetchIntervalInBackground: false,
      retry: 0,
      placeholderData: prev => prev,
      enabled: !!userId,
   });
};

// The endpoint is platform-admin only, so callers pass their own admin check —
// the sidebar renders for org admins too.
export const useGetFraudOpenCount = (enabled = true) => {
   return useQuery({
      queryKey: [FraudWatchQueryKeys.GetOpenCount],
      queryFn: getFraudOpenCount,
      // GET /fraud-watch/count returns a bare number in the body, not { count }.
      // The hermetic e2e mock (and any other non-JSON-number 200) answers unmocked
      // /api/** calls with `{}` — `response?.data ?? 0` only falls back on null/undefined,
      // so that object sailed through as `badgeCount` and React threw "Objects are not
      // valid as a React child" (review — reproduced by admin-manage-trips.spec.ts).
      // Coerce at the boundary instead of trusting the response shape.
      select: (response): number => (typeof response?.data === 'number' ? response.data : Number(response?.data) || 0),
      refetchInterval: 30_000,
      refetchIntervalInBackground: false,
      retry: 0,
      enabled,
   });
};

export const usePostFraudAction = (callBack: MutationParams) => {
   const { onSuccessCallBack, onErrorCallBack, onMutateCallBack, onSettledCallBack } = callBack;
   const queryClient = useQueryClient();

   return useMutation({
      mutationKey: [FraudWatchQueryKeys.PostAction],
      mutationFn: postFraudAction,
      onError: error => {
         onErrorCallBack && onErrorCallBack(error);
      },
      onSuccess: async () => {
         await queryClient.invalidateQueries({ queryKey: [FraudWatchQueryKeys.GetUserDetail] });
         await queryClient.invalidateQueries({ queryKey: [FraudWatchQueryKeys.GetQueue] });
         await queryClient.invalidateQueries({ queryKey: [FraudWatchQueryKeys.GetOpenCount] });

         onSuccessCallBack && onSuccessCallBack();
      },
      onMutate: () => onMutateCallBack && onMutateCallBack(),
      onSettled: () => onSettledCallBack && onSettledCallBack(),
   });
};
